import { lazy, Suspense, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { iniciarSyncEngine } from '@/lib/syncEngine'
import {
  getBrowserAnalyticsConsent,
  setBrowserAnalyticsConsent,
  shouldAskForAnalyticsConsent,
  type AnalyticsConsent,
} from '@/lib/analyticsConsent'
import { isAnalyticsConfigured, isAnalyticsReady, useAnalytics } from '@/lib/posthog'
import { AppLayout } from '@/components/layout/AppLayout.v2'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { AnalyticsConsentModal } from '@/components/analytics/AnalyticsConsentModal'
import { useAuth } from '@/hooks/useAuth'
import { getOnboardingGuardRedirect, getPasswordRecoveryRedirect } from '@/app/authNavigation'
import { ROUTE_PATHS } from '@/app/routeManifest'
import { useAppStore } from '@/stores/appStore'
import { applyThemeClass, resolveTemaPreferencia } from '@/lib/themePreference'
import { supabase } from '@/lib/supabase'
import { AuthProvider } from '@/hooks/useAuth'
import { BillingProvider } from '@/hooks/useBilling'

const LandingPage = lazy(() => import('@/pages/LandingPage'))
const LandingPageVariant = lazy(() => import('@/pages/LandingPageGreen'))
const LandingPageEn = lazy(() => import('@/pages/LandingPageEn'))
const LoginPage = lazy(() => import('@/pages/LoginPage').then(({ LoginPage }) => ({ default: LoginPage })))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(({ ResetPasswordPage }) => ({ default: ResetPasswordPage })))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage').then(({ OnboardingPage }) => ({ default: OnboardingPage })))
const ReportPage = lazy(() => import('@/pages/ReportPage').then(({ ReportPage }) => ({ default: ReportPage })))
const ChatPage = lazy(() => import('@/pages/ChatPage').then(({ ChatPage }) => ({ default: ChatPage })))
const PatternsPage = lazy(() => import('@/pages/PatternsPage').then(({ PatternsPage }) => ({ default: PatternsPage })))
const HistoryPage = lazy(() => import('@/pages/HistoryPage').then(({ HistoryPage }) => ({ default: HistoryPage })))
const StylePage = lazy(() => import('@/pages/StylePage').then(({ StylePage }) => ({ default: StylePage })))
const ClassificationsPage = lazy(() => import('@/pages/ClassificationsPage').then(({ ClassificationsPage }) => ({ default: ClassificationsPage })))
const PerformancePage = lazy(() => import('@/pages/PerformancePage').then(({ PerformancePage }) => ({ default: PerformancePage })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(({ SettingsPage }) => ({ default: SettingsPage })))
const CasosPage = lazy(() => import('@/pages/CasosPage').then(({ CasosPage }) => ({ default: CasosPage })))
const AdminPage = lazy(() => import('@/pages/AdminPage').then(({ AdminPage }) => ({ default: AdminPage })))

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[240px] items-center justify-center px-6 text-sm text-gray-500">
      Cargando...
    </div>
  )
}

function withRouteSuspense(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
}

// Guard para /onboarding — solo accesible si está autenticado
function OnboardingGuard({ children }: { children: ReactNode }) {
  const { estaAutenticado, cargando, usuario } = useAuth()
  const redirectTo = getOnboardingGuardRedirect({ cargando, estaAutenticado, usuario })

  if (cargando) return null
  if (redirectTo) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}

function AppRoutes() {
  const { usuario } = useAuth()

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path={ROUTE_PATHS.landing} element={withRouteSuspense(<LandingPage motion />)} />
      <Route path="/landing-motion" element={withRouteSuspense(<LandingPage motion />)} />
      <Route path="/landing-verde" element={withRouteSuspense(<LandingPageVariant />)} />
      <Route path="/landing-blue" element={withRouteSuspense(<LandingPageVariant />)} />
      <Route path="/landing-azul" element={withRouteSuspense(<LandingPageVariant />)} />
      <Route path="/landing-cian" element={withRouteSuspense(<LandingPageVariant />)} />
      <Route path="/landing-en" element={withRouteSuspense(<LandingPageEn />)} />
      <Route path={ROUTE_PATHS.login} element={withRouteSuspense(<LoginPage />)} />
      <Route path={ROUTE_PATHS.resetPassword} element={withRouteSuspense(<ResetPasswordPage />)} />

      {/* Onboarding — requiere auth pero no onboarding completado */}
      <Route path={ROUTE_PATHS.onboarding} element={withRouteSuspense(<OnboardingGuard><OnboardingPage /></OnboardingGuard>)} />

      {/* Rutas protegidas */}
      <Route
        element={
          <AuthGuard>
            <AppLayout
              usuarioId={usuario?.id}
              nombreUsuario={usuario?.nombre}
              plan={usuario?.plan}
            />
          </AuthGuard>
        }
      >
        <Route path={ROUTE_PATHS.reporte} element={withRouteSuspense(<ReportPage />)} />
        <Route path={ROUTE_PATHS.chat} element={withRouteSuspense(<ChatPage />)} />
        <Route path={ROUTE_PATHS.patrones} element={withRouteSuspense(<PatternsPage />)} />
        <Route path={ROUTE_PATHS.historial} element={withRouteSuspense(<HistoryPage />)} />
        <Route path={ROUTE_PATHS.estilo} element={withRouteSuspense(<StylePage />)} />
        <Route path={ROUTE_PATHS.clasificaciones} element={withRouteSuspense(<ClassificationsPage />)} />
        <Route path={ROUTE_PATHS.rendimiento} element={withRouteSuspense(<PerformancePage />)} />
        <Route path={ROUTE_PATHS.casos} element={withRouteSuspense(<CasosPage />)} />
        <Route path={ROUTE_PATHS.suscripcion} element={<Navigate to={ROUTE_PATHS.ajustes} replace />} />
        <Route path={ROUTE_PATHS.ajustes} element={withRouteSuspense(<SettingsPage />)} />
        <Route path={ROUTE_PATHS.admin} element={withRouteSuspense(<AdminPage />)} />
      </Route>

      {/* Fallback */}
      <Route path={ROUTE_PATHS.fallback} element={<Navigate to={ROUTE_PATHS.landing} replace />} />
    </Routes>
  )
}

function PasswordRecoveryRedirect() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      const redirectTo = getPasswordRecoveryRedirect({
        event,
        currentPath: location.pathname,
      })
      if (redirectTo) navigate(redirectTo, { replace: true })
    })

    return () => subscription.unsubscribe()
  }, [location.pathname, navigate])

  return null
}

function AnalyticsRouteCapture() {
  const location = useLocation()
  const analytics = useAnalytics()

  useEffect(() => {
    analytics?.capture('$pageview', {
      $current_url: `${window.location.origin}${location.pathname}`,
      path: location.pathname,
    })
  }, [analytics, location.pathname])

  return null
}

function AnalyticsIdentity() {
  const analytics = useAnalytics()
  const { usuario, cargando } = useAuth()
  const lastIdentified = useRef<string | null>(null)

  useEffect(() => {
    if (!analytics || cargando) return

    if (!usuario) {
      if (lastIdentified.current) {
        analytics.reset()
        lastIdentified.current = null
      }
      return
    }

    const identityFingerprint = [
      usuario.id,
      usuario.plan,
      usuario.rol,
      usuario.onboarding_completado ? 'onboarded' : 'pending',
    ].join(':')

    if (lastIdentified.current === identityFingerprint) return

    analytics.identify(usuario.id, {
      plan: usuario.plan,
      rol: usuario.rol,
      onboarding_completado: usuario.onboarding_completado,
      modalidades: usuario.modalidades,
    })
    lastIdentified.current = identityFingerprint
  }, [analytics, cargando, usuario])

  return null
}

function AnalyticsConsentGate() {
  const { estaAutenticado, cargando } = useAuth()
  const [consent, setConsent] = useState<AnalyticsConsent>(() => getBrowserAnalyticsConsent())
  const abierto = shouldAskForAnalyticsConsent({
    analyticsConfigured: isAnalyticsConfigured(),
    authenticated: estaAutenticado,
    loading: cargando,
    consent,
  })

  const aceptar = () => {
    setBrowserAnalyticsConsent('accepted')
    setConsent('accepted')
  }

  const rechazar = () => {
    setBrowserAnalyticsConsent('declined')
    setConsent('declined')
  }

  return (
    <AnalyticsConsentModal
      abierto={abierto}
      onAceptar={aceptar}
      onRechazar={rechazar}
    />
  )
}

export default function App() {
  const tema = useAppStore(state => state.tema)
  const [, setAutoThemeTick] = useState(0)

  useEffect(() => {
    iniciarSyncEngine()
  }, [])

  useEffect(() => {
    applyThemeClass(document.documentElement, tema)
    if (tema !== 'auto') return undefined

    const intervalId = window.setInterval(() => {
      applyThemeClass(document.documentElement, tema)
      setAutoThemeTick(tick => tick + 1)
    }, 60_000)

    return () => window.clearInterval(intervalId)
  }, [tema])

  const temaResuelto = resolveTemaPreferencia(tema)
  const toastStyle = temaResuelto === 'claro'
    ? {
        background: '#fffaf0',
        color: '#29231a',
        border: '1px solid #d8c7aa',
        borderRadius: '10px',
        fontSize: '13px',
      }
    : {
        background: '#1f2937',
        color: '#f3f4f6',
        border: '1px solid #374151',
        borderRadius: '10px',
        fontSize: '13px',
      }

  return (
    <BrowserRouter>
      <AuthProvider>
        <BillingProvider>
          <AnalyticsConsentGate />
          {isAnalyticsReady() && <AnalyticsRouteCapture />}
          {isAnalyticsReady() && <AnalyticsIdentity />}
          <PasswordRecoveryRedirect />
          <AppRoutes />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: toastStyle,
              success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
              duration: 3500,
            }}
          />
        </BillingProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
